import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Eye, Code, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth';

const FormManagement = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [formToDelete, setFormToDelete] = useState(null);
  const [embedModalOpen, setEmbedModalOpen] = useState(false);
  const [selectedFormForEmbed, setSelectedFormForEmbed] = useState(null);

  useEffect(() => {
    fetchForms();
  }, []);

  const fetchForms = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/forms', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setForms(response.data || []);
    } catch (err) {
      setError('Failed to fetch forms');
      console.error('Error fetching forms:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!formToDelete) return;

    try {
      await axios.delete(`/api/forms/${formToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setForms(forms.filter(f => f.id !== formToDelete.id));
      setDeleteModalOpen(false);
      setFormToDelete(null);
    } catch (err) {
      alert('Failed to delete form');
      console.error('Error deleting form:', err);
    }
  };

  const handleToggleActive = async (form) => {
    try {
      const updatedForm = {
        name: form.name,
        description: form.description,
        fields: form.fields,
        settings: form.settings,
        active: !form.active,
      };

      const response = await axios.put(`/api/forms/${form.id}`, updatedForm, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setForms(forms.map(f => f.id === form.id ? response.data : f));
    } catch (err) {
      alert('Failed to update form status');
      console.error('Error updating form:', err);
    }
  };

  const getEmbedCode = (formId) => {
    // Production API endpoint
    const backendUrl = 'https://www.visitorstatus.com';

    return `<!-- VisitorStatus Form Widget -->
<div id="vs-form-container"></div>
<script src="${backendUrl}/form-widget.js"></script>
<script>
  VisitorStatusForm.init('${formId}', 'vs-form-container', {
    theme: 'light',
    submitButtonText: 'Submit',
    successMessage: 'Thank you! Your form has been submitted.'
  });
</script>`;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Embed code copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Forms</h1>
          <p className="text-gray-600 mt-1">Create and manage custom forms for your website</p>
        </div>
        <button
          onClick={() => navigate('/forms/new')}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Form
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {forms.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Plus className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No forms yet</h3>
            <p className="text-gray-600 mb-6">
              Get started by creating your first custom form. You can embed it on any website to collect data.
            </p>
            <button
              onClick={() => navigate('/forms/new')}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Form
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Form Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Submissions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {forms.map((form) => (
                <tr key={form.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{form.name}</div>
                      {form.description && (
                        <div className="text-sm text-gray-500">{form.description}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleToggleActive(form)}
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        form.active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {form.active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <button
                      onClick={() => navigate(`/forms/${form.id}/submissions`)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      View Submissions
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(form.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => {
                          setSelectedFormForEmbed(form);
                          setEmbedModalOpen(true);
                        }}
                        className="text-gray-600 hover:text-gray-900"
                        title="Get Embed Code"
                      >
                        <Code className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => navigate(`/forms/${form.id}/edit`)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setFormToDelete(form);
                          setDeleteModalOpen(true);
                        }}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Delete Form</h3>
              <p className="text-sm text-gray-500 mb-6">
                Are you sure you want to delete "{formToDelete?.name}"? This action cannot be undone and will also delete all submissions.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setDeleteModalOpen(false);
                    setFormToDelete(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Embed Code Modal */}
      {embedModalOpen && selectedFormForEmbed && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-2xl max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Embed Code</h3>
              <p className="text-sm text-gray-600 mb-4">
                Copy and paste this code into your website where you want the form to appear:
              </p>
              <div className="bg-gray-50 p-4 rounded-md border border-gray-200 mb-4">
                <pre className="text-xs text-gray-800 overflow-x-auto whitespace-pre-wrap break-all">
                  {getEmbedCode(selectedFormForEmbed.id)}
                </pre>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setEmbedModalOpen(false);
                    setSelectedFormForEmbed(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
                <button
                  onClick={() => copyToClipboard(getEmbedCode(selectedFormForEmbed.id))}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Copy to Clipboard
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormManagement;
